/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { useTheme } from '../theme/ThemeContext';
import { useAlertDialog } from '../components/common/AlertDialogContext';
import ThemedButton from '../components/common/ThemedButton';
import ThemedTextArea from '../components/common/ThemedTextArea';
import FieldWrapper from '../components/common/FieldWrapper';
import {
  FileField, ImageField, inlineButton, Row, Section, SelectField, TextField,
} from '../components/athlohive/EventFormFields';
import { describeBatchError } from '../components/athlohive/describeBatchError';
import { readLookup, toOptions } from '../components/athlohive/lookupOptions';
import {
  buildOrganizationBatch, hydrateOrganizationForm, initialOrganizationForm,
  validateOrganizationForm, VERIFICATION_STATUSES,
} from '../components/athlohive/organizationPayloads';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/** Creating or editing one organizer. */

const STATUS_LABELS = {
  PENDING: 'Pending — not yet reviewed',
  APPROVED: 'Approved — may host events',
  REJECTED: 'Rejected',
  BLOCKED: 'Blocked',
};

export default function OrganizationFormPage() {
  const { id: applicationId, organizationRecordId } = useParams();
  const isEdit = !!organizationRecordId;
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { showAlertDialog } = useAlertDialog();

  const [form, setForm] = useState(initialOrganizationForm);
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const setField = (field) => (value) => setForm((current) => ({ ...current, [field]: value }));

  const { data: application, isLoading: loadingApplication } = useQuery({
    queryKey: ['application', applicationId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/details/${applicationId}/`, { method: 'GET', signal }
      );
      if (!resp.ok) throw new Error('Could not load this application.');
      return resp.json();
    },
    enabled: !!applicationId,
    staleTime: 60_000,
  });

  const organizationId = application?.organization?.id
    ?? application?.organization_id
    ?? (typeof application?.organization === 'number' ? application.organization : undefined);

  const fetchTable = async (tableName, signal, filters) => {
    const resp = await apiHelper.apiCall('/arcee/application/table/fetch/', {
      method: 'POST',
      signal,
      body: JSON.stringify({
        table_name: tableName,
        organization_id: organizationId,
        application_id: Number(applicationId),
        page_size: 200,
        ...(filters ? { filters } : {}),
      }),
    });
    if (!resp.ok) throw new Error(`Could not load ${tableName}.`);
    return readLookup(await resp.json());
  };

  const types = useQuery({
    queryKey: ['organizationTypes', applicationId, organizationId],
    queryFn: ({ signal }) => fetchTable('organization_type', signal),
    enabled: !!organizationId && !!applicationId,
    staleTime: 60_000,
  });

  // Read through the batch endpoint rather than table/fetch: that endpoint
  // validates filter columns against the table's declared fields, and `id` is
  // the implicit primary key rather than a declared one, so filtering by it is
  // rejected with "Column 'id' not found in this table". batch_read accepts it.
  const existing = useQuery({
    queryKey: ['organizationRecord', applicationId, organizationId, organizationRecordId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall('/arcee/application/table/read/records/', {
        method: 'POST',
        signal,
        body: JSON.stringify({
          organization_id: organizationId,
          application_id: Number(applicationId),
          operations: [
            { ref: 'o', table: 'organization', where: { id: Number(organizationRecordId) } },
          ],
        }),
      });
      if (!resp.ok) throw new Error('Could not load this organization.');
      const body = await resp.json();
      const block = (body.results || body)?.o;
      const rows = block && block.status === 'ok' ? block.data : [];
      return rows[0] || null;
    },
    enabled: isEdit && !!organizationId && !!applicationId,
    staleTime: 0,
  });

  useEffect(() => {
    if (!isEdit || !existing.data) return;
    const hydrated = hydrateOrganizationForm(existing.data);
    if (hydrated) setForm(hydrated);
  }, [isEdit, existing.data]);

  useEffect(() => { setErrors(validateOrganizationForm(form)); }, [form]);

  const save = useMutation({
    mutationFn: async () => {
      const resp = await apiHelper.apiCall('/arcee/application/table/save/records/', {
        method: 'POST',
        body: JSON.stringify(buildOrganizationBatch(form, {
          organizationId, applicationId: Number(applicationId),
        })),
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok) throw result;
      return result;
    },
    onSuccess: () => {
      showAlertDialog({
        title: isEdit ? 'Organization updated' : 'Organization added',
        message: isEdit
          ? 'The changes were saved.'
          : 'It can now be chosen as the host of an event.',
        type: 'success',
        okLabel: 'Done',
        onOk: () => navigate(`/dashboard/applications/${applicationId}/athlohive/organizations`),
      });
    },
    onError: (error) => setSubmitError(describeBatchError(error)),
  });

  const submit = () => {
    const allErrors = validateOrganizationForm(form);
    if (Object.keys(allErrors).length) {
      setShowErrors(true);
      return;
    }
    setSubmitError(null);
    save.mutate();
  };

  const visible = showErrors ? errors : {};
  const listUrl = `/dashboard/applications/${applicationId}/athlohive/organizations`;

  if (loadingApplication || (isEdit && existing.isLoading)) {
    return <div style={{ padding: 24 }}>Loading…</div>;
  }

  if (isEdit && (existing.isError || existing.data === null)) {
    return (
      <div style={{ padding: 24, color: '#d53b3b' }}>
        This organization could not be loaded. It may have been deleted.
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
      <div style={{ marginBottom: 6, fontSize: 13, color: '#6b7280' }}>
        <span style={{ cursor: 'pointer' }} onClick={() => navigate(listUrl)}>
          Organizations
        </span>
        {isEdit ? ' / Edit' : ' / Add'}
      </div>
      <h2 style={{ color: theme.primary, marginBottom: 18 }}>
        {isEdit ? `Edit ${form.name || 'organization'}` : 'Add organization'}
      </h2>

      <Section title="About" subtitle="Who runs the events.">
        <Row>
          <TextField
            label="Name" required error={visible.name}
            value={form.name} onChange={setField('name')}
            placeholder="RunBLR Collective"
          />
          <SelectField
            label="Type" required error={visible.organization_type_id}
            value={form.organization_type_id} onChange={setField('organization_type_id')}
            options={toOptions(types.data)}
            placeholder="Choose a type…"
          />
        </Row>
        <FieldWrapper label="About">
          <ThemedTextArea
            rows={4}
            value={form.about_information}
            placeholder="What this organization does, and the events it runs."
            onChange={(e) => setField('about_information')(e.target.value)}
          />
        </FieldWrapper>
        <ImageField
          label="Logo" value={form.logo_url} onChange={setField('logo_url')} name="logo_url"
        />
      </Section>

      <Section title="Address" subtitle="Where the organization is registered.">
        <TextField
          label="Address" required error={visible.address_line}
          value={form.address_line} onChange={setField('address_line')}
        />
        <Row>
          <TextField label="City" required error={visible.city}
            value={form.city} onChange={setField('city')} />
          <TextField label="State" required error={visible.state}
            value={form.state} onChange={setField('state')} />
        </Row>
        <Row>
          <TextField label="Country" required error={visible.country}
            value={form.country} onChange={setField('country')} />
          <TextField label="PIN code" required error={visible.pin_code}
            value={form.pin_code} onChange={setField('pin_code')} />
        </Row>
      </Section>

      <Section title="Contact" subtitle="How participants and the platform reach them.">
        <Row>
          <TextField
            label="Contact email" required type="email" error={visible.contact_email}
            value={form.contact_email} onChange={setField('contact_email')}
          />
          <TextField
            label="Support email" required type="email" error={visible.support_email}
            value={form.support_email} onChange={setField('support_email')}
            hint="Shown to participants who need help."
          />
        </Row>
        <Row>
          <TextField label="Phone" value={form.contact_phone} onChange={setField('contact_phone')} />
          <TextField
            label="Website" value={form.official_website_url}
            onChange={setField('official_website_url')} placeholder="https://…"
          />
        </Row>
        <TextField
          label="Government id" value={form.government_id} onChange={setField('government_id')}
          hint="Registration or tax id, if you hold one."
        />
        <FileField
          label="Government id proof"
          value={form.government_id_proof}
          onChange={setField('government_id_proof')}
          name="government_id_proof"
          hint="A scan or photo of the document behind the id above — image or PDF. Stored privately, not on the public event page."
          organizationId={organizationId}
          applicationId={applicationId}
        />
      </Section>

      <Section
        title="Verification"
        subtitle="Only an approved organization should be hosting public events."
      >
        <SelectField
          label="Status" required error={visible.verification_status}
          value={form.verification_status} onChange={setField('verification_status')}
          options={VERIFICATION_STATUSES.map((status) => ({
            value: status, label: STATUS_LABELS[status] || status,
          }))}
          placeholder={null}
        />
        {form.verification_status === 'REJECTED' && (
          <FieldWrapper label="Reason for rejection" required error={visible.rejection_reason}>
            <ThemedTextArea
              rows={3} error={visible.rejection_reason} value={form.rejection_reason}
              onChange={(e) => setField('rejection_reason')(e.target.value)}
            />
          </FieldWrapper>
        )}
        {form.verification_status === 'BLOCKED' && (
          <FieldWrapper label="Reason for blocking" required error={visible.blocked_reason}>
            <ThemedTextArea
              rows={3} error={visible.blocked_reason} value={form.blocked_reason}
              onChange={(e) => setField('blocked_reason')(e.target.value)}
            />
          </FieldWrapper>
        )}
      </Section>

      {submitError && (
        <div style={{
          border: '1px solid #d53b3b', background: '#fdf2f2', color: '#991b1b',
          borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 14,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Nothing was saved.</div>
          <div>{submitError.message}</div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <ThemedButton
          type="button" variant="outlined" style={inlineButton}
          onClick={() => navigate(listUrl)}
        >
          Cancel
        </ThemedButton>
        <ThemedButton
          type="button" style={inlineButton}
          disabled={save.isPending}
          onClick={submit}
        >
          {save.isPending ? 'Saving…' : (isEdit ? 'Save changes' : 'Add organization')}
        </ThemedButton>
      </div>
    </div>
  );
}
